import { useState, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { Text, Field, InputDropdown, Icon, PrimaryButton, Button, InputV2, Alert } from '@artprov/design-system'
import SafeSearchBar from '@/common/components/SafeSearchBar'
import { useMiPerfilForm } from './hooks/useMiPerfilForm.js'
import { useCargosAreas } from './hooks/useCargosAreas.js'
import { useCuentasUsuario } from './hooks/useCuentasUsuario.js'
import { useCuentasPendientes } from './hooks/useCuentasPendientes.js'
import { useDatosPersonales } from './hooks/useDatosPersonales.js'
import {
  TIPOS_TELEFONO,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  TOAST_MESSAGES,
  ESTADO_PENDIENTE,
  MOTIVO_RECHAZO_OTROS,
} from './constants/formConstants.js'
import CuentasTable from './components/CuentasTable'
import ModalAsociarCuenta from './components/ModalAsociarCuenta'
import ModalDescartarCambios from './components/ModalDescartarCambios'
import ModalGuardarCambios from './components/ModalGuardarCambios'
import ModalEliminarCuenta from './components/ModalEliminarCuenta'
import ModalDdjj from '@/common/components/ddjj/ModalDdjj'
import { buildCuentasRows } from './util/buildCuentasRows'
import { getCuentasColumns } from './constants/columns'
import { TEXTOS_DDJJ_MIS_CUENTAS, VALIDACION_DDJJ_MIS_CUENTAS } from './constants/textosDdjjMisCuentas'
import { buildDatosPersonalesPayload, buildAsociarCuentaPayload } from './util/mappers'
import {
  EXIT_ACTIONS,
  EXIT_STEPS,
  getPreviousPageTarget,
  resolveDiscardStep,
  resolveExitStep,
} from './util/exitNavigation'
import { puedeEliminarCuenta, quitarSolicitudVisual } from './util/eliminarCuenta'
import {
  updateDatosPersonales,
  asociarCuenta,
  bajaRelacionContrato,
  rechazarSolicitud,
} from '@/common/utils/services/usuarios.service'
import { getIdUsuario, getEmailUsuario } from '@/auth/auth.utils'
import { useIsMobileMenu } from '@/common/ds/Menu'

/**
 * Persiste la baja de una cuenta en el backend. El servicio depende del estado
 * del registro: una solicitud Pendiente se rechaza (motivo "Otros") y un
 * contrato Activo se desasocia (DELETE /usuarios/contratos, PBI 84320).
 * @returns {boolean} true si la baja se persistió correctamente
 */
const procesarBajaCuenta = async (cuenta, idUsuario) => {
  const esSolicitudPendiente = cuenta.estado === ESTADO_PENDIENTE

  const result = esSolicitudPendiente
    ? await rechazarSolicitud({
        idSolicitud: cuenta.idSolicitud,
        idUsu: Number(idUsuario),
        idRechazo: MOTIVO_RECHAZO_OTROS.id,
        motRechazo: MOTIVO_RECHAZO_OTROS.detalle,
      })
    : await bajaRelacionContrato({
        idUsuario: Number(idUsuario),
        contrato: cuenta.contrato,
        usuAlta: getEmailUsuario(),
      })

  return result.code >= 200 && result.code < 300
}

/**
 * Persiste los datos personales del formulario.
 * @returns {boolean} true si se guardaron correctamente
 */
const guardarDatosPersonales = async (formData, idUsuario) => {
  const payload = buildDatosPersonalesPayload(formData, idUsuario)
  const result = await updateDatosPersonales(payload)
  return result.code === 200
}

/**
 * Persiste todas las bajas pendientes, en orden secuencial. Las fallidas
 * permanecen en el estado pendiente para permitir un reintento.
 * @returns {{ huboError: boolean, huboExito: boolean }}
 */
const persistirBajasPendientes = async (cuentas, idUsuario, quitarEliminada) => {
  let huboError = false
  let huboExito = false

  const persistirBaja = async (cuenta) => {
    try {
      const exito = await procesarBajaCuenta(cuenta, idUsuario)
      if (exito) {
        quitarEliminada(cuenta)
        huboExito = true
      } else {
        huboError = true
      }
    } catch (error) {
      console.error('Error al dar de baja la relación:', error)
      huboError = true
    }
  }

  // Encadenamiento de promesas para mantener el orden secuencial de las bajas
  await cuentas.reduce((previa, cuenta) => previa.then(() => persistirBaja(cuenta)), Promise.resolve())

  return { huboError, huboExito }
}

export default function MiPerfil() {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobileMenu()
  const { openMobileProfile } = useOutletContext() ?? {}

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  // Botón que abrió el modal de descartar cambios: EXIT_ACTIONS.BACK | EXIT_ACTIONS.CANCEL | null
  const [discardAction, setDiscardAction] = useState(null)
  const [isConfirmSaveModalOpen, setIsConfirmSaveModalOpen] = useState(false)

  const [isModalAsociarOpen, setIsModalAsociarOpen] = useState(false)

  // Solicitudes de asociación pendientes de persistir (solo visual en la grilla).
  // Se agregan al confirmar el modal y se persisten al presionar "Guardar cambios".
  // Si el usuario recarga (F5) o navega fuera, se descartan (igual que la eliminación de cuenta).
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([])

  // Estado de baja de cuenta (contrato activo o solicitud pendiente)
  const [cuentaAEliminar, setCuentaAEliminar] = useState(null)
  const [isEliminarModalOpen, setIsEliminarModalOpen] = useState(false)

  // Estado único de toast: { variant: 'success' | 'error', message: string } | null
  const [toast, setToast] = useState(null)

  // Estado para loading del guardado
  const [isSaving, setIsSaving] = useState(false)

  // PBI 81147 (D8): contrato de la fila cuyo ícono «Subir documento» abrió el modal
  // de carga de la DDJJ. null = modal cerrado.
  const [contratoDdjj, setContratoDdjj] = useState(null)

  const { cargoOptions, areaOptions } = useCargosAreas()

  // Hook para datos personales del usuario
  const {
    hasFetched: hasFetchedDatosPersonales,
    apellido: apellidoReal,
    nombre: nombreReal,
    correoElectronico: correoReal,
    tipoTelefonoPrincipal: tipoTelPrincipalReal,
    numeroTelefonoPrincipal: numTelPrincipalReal,
    tipoTelefonoAlternativo: tipoTelAlternativoReal,
    numeroTelefonoAlternativo: numTelAlternativoReal,
    tipoTelefonoPrincipalId: tipoTelPrincipalIdReal,
    updateDatos: updateDatosPersonalesState,
  } = useDatosPersonales()

  // Datos crudos del usuario para el payload de asociación de cuenta
  const datosPersonales = useMemo(
    () => ({
      correoElectronico: correoReal,
      apellido: apellidoReal,
      nombre: nombreReal,
      tipoTelefonoPrincipalId: tipoTelPrincipalIdReal,
      numeroTelefonoPrincipal: numTelPrincipalReal,
    }),
    [correoReal, apellidoReal, nombreReal, tipoTelPrincipalIdReal, numTelPrincipalReal]
  )

  // Operaciones de cuentas pendientes de guardar (solo visuales hasta persistir)
  const { cuentasEliminadas, hasCuentasPendientes, marcarEliminada, quitarEliminada, limpiarPendientes } =
    useCuentasPendientes()

  const {
    contratos,
    todasLasCuentas,
    headers: cuentasHeaders,
    isLoading: cuentasLoading,
    error: cuentasError,
    searchValue: cuentasSearchValue,
    setSearchValue: setCuentasSearchValue,
    onPageChange: handleCuentasPageChange,
    esAdminARToHyS,
    tieneAlgunaCuentaMedica,
    refreshContratos,
  } = useCuentasUsuario(cuentasEliminadas)

  // Caso 1: ninguna cuenta con perfil médico -> sin columnas Médico/Subir documento.
  // Caso 2: al menos una cuenta con perfil médico -> se muestran ambas columnas.
  const cuentasColumns = useMemo(() => getCuentasColumns(tieneAlgunaCuentaMedica), [tieneAlgunaCuentaMedica])

  const showToast = useCallback((variant, message) => {
    setToast({ variant, message })
    setTimeout(() => setToast(null), 4000)
  }, [])

  // Acción del tachito en la grilla: abre el modal de confirmación de baja.
  const handleEliminarCuenta = useCallback(
    (item) => {
      // Las solicitudes visuales (agregadas desde el modal pero aún no
      // persistidas) no tienen idSolicitud, pero se eliminan solo de la grilla.
      if (!puedeEliminarCuenta(item)) {
        showToast('error', TOAST_MESSAGES.ELIMINAR_CUENTA_ERROR)
        return
      }

      setCuentaAEliminar(item)
      setIsEliminarModalOpen(true)
    },
    [showToast]
  )

  // Filas de la grilla: cuentas reales + solicitudes pendientes visuales (no persistidas)
  const cuentasRows = useMemo(() => {
    const filasPendientes = solicitudesPendientes.map((solicitud) => ({
      ...solicitud,
      estado: 'Pendiente',
    }))
    return buildCuentasRows([...filasPendientes, ...contratos], {
      onEliminar: handleEliminarCuenta,
      onSubirDocumento: (item) => setContratoDdjj(item.contrato),
    })
  }, [contratos, solicitudesPendientes, handleEliminarCuenta])

  const handleAsociarNuevaCuenta = useCallback(() => {
    setIsModalAsociarOpen(true)
  }, [])

  // Cancelación de la baja: solo cierra el modal, sin llamar servicios ni mutar estado
  const handleCancelarEliminar = useCallback(() => {
    setIsEliminarModalOpen(false)
    setCuentaAEliminar(null)
  }, [])

  // El modal solo valida: agrega la solicitud como fila visual "Pendiente"
  // en la grilla (en memoria) y cierra. La persistencia ocurre en "Guardar cambios".
  const handleModalSubmit = useCallback((formData) => {
    setIsModalAsociarOpen(false)
    setSolicitudesPendientes((prev) => [
      ...prev,
      {
        // Datos necesarios para persistir la solicitud en "Guardar cambios"
        cuit: formData.cuit,
        cargoId: formData.cargoId,
        areaAfinidadId: formData.areaAfinidadId,
        // Datos para la fila visual de la grilla.
        // Si el modal pudo validar la CUIT contra Core Company, usamos la razón
        // social y el contrato reales; si no, dejamos el placeholder de siempre.
        razonSocial: formData.empresa?.razonSocial || '',
        contrato: formData.empresa?.contrato ?? `solicitud-visual-${prev.length + 1}`,
        medico: 'No',
        estado: 'Pendiente',
        esSolicitudVisual: true,
      },
    ])
  }, [])

  // Confirmación del modal de baja: la eliminación queda pendiente (solo visual)
  // hasta que el usuario presione "Guardar cambios". No se llama al backend aquí.
  // Si el usuario cierra el modal sin confirmar, no se genera ninguna eliminación.
  const handleConfirmarEliminar = useCallback(() => {
    if (!cuentaAEliminar) return

    if (cuentaAEliminar.esSolicitudVisual) {
      // Solicitud visual: se quita directamente de la grilla, sin backend
      setSolicitudesPendientes((prev) => quitarSolicitudVisual(prev, cuentaAEliminar))
    } else {
      marcarEliminada(cuentaAEliminar)
    }

    setIsEliminarModalOpen(false)
    setCuentaAEliminar(null)
  }, [cuentaAEliminar, marcarEliminada])

  const datosPersonalesIniciales = useMemo(
    () => ({
      apellido: apellidoReal,
      nombre: nombreReal,
      tipoTelefonoPrincipal: tipoTelPrincipalReal,
      numeroTelefonoPrincipal: numTelPrincipalReal,
      tipoTelefonoAlternativo: tipoTelAlternativoReal,
      numeroTelefonoAlternativo: numTelAlternativoReal,
    }),
    [apellidoReal, nombreReal, tipoTelPrincipalReal, numTelPrincipalReal, tipoTelAlternativoReal, numTelAlternativoReal]
  )

  const {
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
    isFormValid,
    hasChanges,
    handleTipoTelefonoAlternativoChange,
    handleNumeroTelefonoAlternativoChange,
    handleApellidoChange,
    handleNombreChange,
    handleTipoTelefonoPrincipalChange,
    handleNumeroTelefonoPrincipalChange,
    handleBlur,
    handlePhoneDataChange,
    handleAltPhoneDataChange,
    getFieldError,
    getFieldStatus,
    getPhoneHints,
    getAltPhoneStatus,
    getAltPhoneHints,
    validateAllFields,
    resetForm,
    syncInitialData,
  } = useMiPerfilForm(datosPersonalesIniciales, hasFetchedDatosPersonales)

  const correoElectronico = correoReal

  // Cambios sin guardar: datos personales, bajas de cuentas y solicitudes de asociación visuales
  const hasUnsavedChanges = hasChanges || hasCuentasPendientes || solicitudesPendientes.length > 0

  const canOpenMobileProfile = Boolean(isMobile && openMobileProfile)

  const runExitStep = (step, action) => {
    switch (step) {
      case EXIT_STEPS.OPEN_DISCARD_MODAL:
        setDiscardAction(action)
        setIsCancelModalOpen(true)
        return
      case EXIT_STEPS.OPEN_MOBILE_PROFILE:
        openMobileProfile()
        return
      case EXIT_STEPS.GO_TO_PREVIOUS_PAGE:
        void navigate(getPreviousPageTarget(location.key))
        return
      default:
        return
    }
  }

  const handleExit = (action) => {
    runExitStep(resolveExitStep({ action, hasChanges: hasUnsavedChanges, canOpenMobileProfile }), action)
  }

  const handleBack = () => handleExit(EXIT_ACTIONS.BACK)

  const handleCancel = () => handleExit(EXIT_ACTIONS.CANCEL)

  const handleConfirmDiscard = () => {
    resetForm()
    // Descarta también las operaciones de cuentas pendientes (bajas y altas
    // solo visuales), restaurando la grilla al estado del servidor
    limpiarPendientes()
    setIsCancelModalOpen(false)
    runExitStep(resolveDiscardStep({ action: discardAction, canOpenMobileProfile }))
    setDiscardAction(null)
  }

  const handleGuardarCambios = () => {
    const isValid = validateAllFields()

    if (!isValid) {
      return
    }

    setIsConfirmSaveModalOpen(true)
  }

  // Punto único de persistencia. Procesa, en orden:
  // 1) Bajas pendientes de cuentas (solicitudes Pendiente -> rechazo, contratos -> baja)
  // 2) Datos personales del formulario (solo si hay cambios)
  // Las altas (asociaciones) se persisten al confirmar el modal, no aquí.
  // Ante errores parciales, las operaciones fallidas permanecen pendientes para
  // permitir un nuevo intento, y se muestra un mensaje de error genérico.
  const handleConfirmSave = async () => {
    setIsConfirmSaveModalOpen(false)
    setIsSaving(true)

    let huboErrorCuentas = false
    let huboExitoCuentas = false
    let huboErrorDatos = false

    try {
      const idUsuario = getIdUsuario()
      if (!idUsuario) {
        throw new Error('No se encontró el ID de usuario')
      }

      // 1) Bajas pendientes de cuentas (solicitudes Pendiente -> rechazo, contratos -> baja)
      const resultadoBajas = await persistirBajasPendientes(cuentasEliminadas, idUsuario, quitarEliminada)
      huboErrorCuentas = resultadoBajas.huboError
      huboExitoCuentas = resultadoBajas.huboExito

      // 2) Datos personales: solo si el formulario tiene cambios
      if (hasChanges) {
        try {
          const formData = {
            apellido,
            nombre,
            tipoTelefonoPrincipal,
            numeroTelefonoPrincipal,
            tipoTelefonoAlternativo,
            numeroTelefonoAlternativo,
          }

          const exito = await guardarDatosPersonales(formData, idUsuario)

          if (exito) {
            updateDatosPersonalesState(formData)
            syncInitialData(formData)
          } else {
            huboErrorDatos = true
          }
        } catch (error) {
          console.error('Error al guardar datos personales:', error)
          huboErrorDatos = true
        }
      }

      // 3) Altas pendientes: solicitudes de asociación agregadas como filas visuales
      const exitoSolicitudes = await persistirSolicitudesPendientes()
      if (!exitoSolicitudes) {
        huboErrorCuentas = true
      }

      // Resultado final: solo se informa éxito si todo se persistió correctamente
      if (huboErrorCuentas) {
        showToast('error', TOAST_MESSAGES.GUARDAR_CUENTAS_ERROR)
      } else if (huboErrorDatos) {
        showToast('error', TOAST_MESSAGES.ERROR)
      } else {
        showToast('success', TOAST_MESSAGES.SUCCESS)
      }

      // Resincroniza la grilla con el servidor si al menos una operación de
      // cuentas se persistió
      if (huboExitoCuentas || exitoSolicitudes) {
        refreshContratos()
      }
    } catch (error) {
      console.error('Error al guardar los cambios:', error)
      showToast('error', TOAST_MESSAGES.ERROR)
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Persiste las solicitudes de asociación pendientes (filas visuales de la grilla)
   * llamando al servicio existente asociarCuenta por cada una.
   * @returns {boolean} true si no había solicitudes o todas se persistieron con éxito
   */
  const persistirSolicitudesPendientes = async () => {
    if (solicitudesPendientes.length === 0) {
      return true
    }

    const emailUsuario = getEmailUsuario()

    try {
      for (const solicitud of solicitudesPendientes) {
        const payload = buildAsociarCuentaPayload(
          { cuit: solicitud.cuit, cargoId: solicitud.cargoId, areaAfinidadId: solicitud.areaAfinidadId },
          datosPersonales,
          emailUsuario
        )
        const result = await asociarCuenta(payload)

        if (!(result.code >= 200 && result.code < 300)) {
          // Error de negocio devuelto por el backend (re-validación en el guardado)
          const backendMessage = (result.message || '').trim()
          let mensajeError
          if (Object.values(TOAST_MESSAGES.BACKEND_MESSAGES).includes(backendMessage)) {
            mensajeError = TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR_CUIT_NO_CLIENTE
          } else if (backendMessage) {
            mensajeError = TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR_NEGOCIO
          } else {
            mensajeError = TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR
          }
          showToast('error', mensajeError)
          return false
        }
      }

      // Todas persistidas: limpiar filas visuales y refrescar la grilla
      setSolicitudesPendientes([])
      refreshContratos()
      return true
    } catch (error) {
      // Excepción de red / timeout: se mantienen las filas visuales para reintentar
      console.error('Error al persistir solicitudes de asociación:', error)
      showToast('error', TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR_RED)
      return false
    }
  }

  const handleCloseToast = () => {
    setToast(null)
  }

  return (
    <div className="min-h-[calc(100vh-168px)] px-4 md:px-0 pb-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="xs"
          startIcon={<Icon icon="keyboard_arrow_left" size="small" />}
          className="self-start leading-none"
          onClick={handleBack}
        >
          Volver
        </Button>
      </div>

      <div className="mb-6">
        <Text variant="h6" className="text-primary-20 font-semibold">
          Mi perfil
        </Text>
      </div>

      <div className="bg-[#F5F5F5] rounded-lg p-6">
        <Text variant="h6" className="text-primary-10 font-semibold mb-6">
          Datos personales
        </Text>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-4">
            <div className="md:col-span-4">
              <Field label={FORM_LABELS.APELLIDO} errorMessage={getFieldError('apellido')} isRequired={true}>
                <InputV2
                  placeholder={FORM_PLACEHOLDERS.APELLIDO}
                  value={apellido}
                  onChange={handleApellidoChange}
                  onBlur={() => handleBlur('apellido', apellido)}
                  startIcon=""
                  endIcon=""
                />
              </Field>
            </div>

            <div className="md:col-span-4">
              <Field label={FORM_LABELS.NOMBRE} errorMessage={getFieldError('nombre')} isRequired={true}>
                <InputV2
                  placeholder={FORM_PLACEHOLDERS.NOMBRE}
                  value={nombre}
                  onChange={handleNombreChange}
                  onBlur={() => handleBlur('nombre', nombre)}
                  startIcon=""
                  endIcon=""
                />
              </Field>
            </div>

            <div className="md:col-span-4">
              <Field
                variant="email"
                label={FORM_LABELS.CORREO_ELECTRONICO}
                placeholder={FORM_PLACEHOLDERS.CORREO_ELECTRONICO}
                value={correoElectronico}
                onChange={() => {}}
                isDisabled={true}
                noVariantIcon
                noVariantHints
              />
            </div>

            <div className="md:col-span-6">
              <Field
                label={FORM_LABELS.TIPO_TELEFONO_PRINCIPAL}
                isRequired={true}
                className="[&_label]:mb-0.3"
                errorMessage={getFieldError('tipoTelefonoPrincipal')}
              >
                <InputDropdown
                  placeholder={FORM_PLACEHOLDERS.SELECCIONAR}
                  value={tipoTelefonoPrincipal}
                  defaultSelection={false}
                  onChange={(e) => {
                    const newValue = e?.value ?? ''
                    handleTipoTelefonoPrincipalChange(newValue)
                  }}
                  fullWidth
                >
                  {TIPOS_TELEFONO.filter((opt) => opt.id !== '').map((opt) => (
                    <InputDropdown.Option
                      key={opt.id}
                      value={opt.descripcion}
                      selected={opt.descripcion === tipoTelefonoPrincipal}
                    >
                      {opt.descripcion}
                    </InputDropdown.Option>
                  ))}
                </InputDropdown>
              </Field>
            </div>

            <div className="md:col-span-6">
              <Field
                variant="phone"
                label={FORM_LABELS.NUMERO_TELEFONO_PRINCIPAL}
                placeholder={FORM_PLACEHOLDERS.NUMERO_TELEFONO_PRINCIPAL}
                value={numeroTelefonoPrincipal}
                onChange={handleNumeroTelefonoPrincipalChange}
                onBlur={() => handleBlur('numeroTelefonoPrincipal', numeroTelefonoPrincipal)}
                onPhoneDataChange={handlePhoneDataChange}
                status={getFieldStatus('numeroTelefonoPrincipal')}
                hints={getPhoneHints()}
                errorMessage={getFieldError('numeroTelefonoPrincipal')}
                noVariantIcon
                isRequired={true}
                className="[&_button]:hidden [&_[data-slot=info]]:hidden"
              />
            </div>

            <div className="md:col-span-6">
              <Field
                label={FORM_LABELS.TIPO_TELEFONO_ALTERNATIVO}
                errorMessage={getFieldError('tipoTelefonoAlternativo')}
                className="[&_label]:mb-0.3"
              >
                <InputDropdown
                  placeholder={FORM_PLACEHOLDERS.SELECCIONAR}
                  value={tipoTelefonoAlternativo}
                  status={getAltPhoneStatus('tipoTelefonoAlternativo')}
                  defaultSelection={false}
                  onChange={(e) => {
                    const rawValue = e?.value ?? ''
                    // "Seleccionar" equivale a vacío: el tipo alternativo vuelve a considerarse sin cargar
                    const newValue = rawValue === 'Seleccionar' ? '' : rawValue
                    handleTipoTelefonoAlternativoChange(newValue)
                  }}
                  fullWidth
                >
                  {TIPOS_TELEFONO.map((opt) => (
                    <InputDropdown.Option
                      key={opt.id}
                      value={opt.descripcion}
                      selected={opt.descripcion === tipoTelefonoAlternativo}
                    >
                      {opt.descripcion}
                    </InputDropdown.Option>
                  ))}
                </InputDropdown>
              </Field>
            </div>

            <div className="md:col-span-6">
              <Field
                variant="phone"
                label={FORM_LABELS.NUMERO_TELEFONO_ALTERNATIVO}
                placeholder={FORM_PLACEHOLDERS.NUMERO_TELEFONO_ALTERNATIVO}
                value={numeroTelefonoAlternativo}
                onChange={handleNumeroTelefonoAlternativoChange}
                onPhoneDataChange={handleAltPhoneDataChange}
                status={getAltPhoneStatus('numeroTelefonoAlternativo')}
                isInvalid={getAltPhoneStatus('numeroTelefonoAlternativo') === 'error'}
                hints={getAltPhoneHints()}
                errorMessage={getFieldError('numeroTelefonoAlternativo')}
                noVariantIcon
              />
            </div>
          </div>
        </div>
      </div>

      {esAdminARToHyS === false && (
        <div className="bg-[#F5F5F5] rounded-lg p-6 mt-6">
          <Text variant="h6" className="text-primary-10 font-semibold mb-6">
            Mis cuentas
          </Text>

          <div className="bg-white rounded-lg p-6 border border-gray-200">
            {/* Barra de búsqueda y botón */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <SafeSearchBar
                placeholder="Buscar por razón social, nombre o CUIT"
                searchValue={cuentasSearchValue}
                setSearchValue={setCuentasSearchValue}
                opciones={[]}
                setSelected={() => {}}
                cargando={false}
                noResult={false}
                classNameContainer="w-full md:w-[420px]"
              />
              <Button
                variant="ghost"
                size="sm"
                startIcon={<Icon icon="add" size="small" />}
                onClick={handleAsociarNuevaCuenta}
                className="text-primary-10 whitespace-nowrap"
              >
                Asociar nueva cuenta
              </Button>
            </div>

            {/* Tabla de cuentas */}
            <CuentasTable
              columns={cuentasColumns}
              headers={cuentasHeaders}
              tableData={cuentasRows}
              onPageChange={handleCuentasPageChange}
              isLoading={cuentasLoading}
              hasSearchFilter={cuentasSearchValue.trim().length > 0}
              error={cuentasError}
            />
          </div>
        </div>
      )}

      <div className="flex mt-6 justify-end gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={handleCancel} isDisabled={isSaving}>
          Cancelar
        </Button>

        <PrimaryButton
          onClick={handleGuardarCambios}
          size="small"
          disabled={!isFormValid || !hasUnsavedChanges || isSaving}
        >
          {isSaving ? 'Guardando...' : 'Guardar cambios'}
        </PrimaryButton>
      </div>

      {/* Modal de confirmación para guardar cambios */}
      <ModalGuardarCambios
        open={isConfirmSaveModalOpen}
        setOpen={setIsConfirmSaveModalOpen}
        onCancel={() => setIsConfirmSaveModalOpen(false)}
        onConfirm={handleConfirmSave}
      />

      {/* Modal de descartar cambios */}
      <ModalDescartarCambios
        open={isCancelModalOpen}
        setOpen={setIsCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        onConfirm={handleConfirmDiscard}
      />

      {/* Toast único con variant dinámico: success (solicitud exitosa) o error (los 3 escenarios de error) */}
      {toast &&
        createPortal(
          <div className="fixed top-4 right-4 w-[400px]" style={{ zIndex: 99999 }}>
            <Alert
              variant={toast.variant}
              iconStyle="default"
              title=""
              description={toast.message}
              descriptionStyle="whitespace-normal break-normal"
              interactive={true}
              buttonText=""
              onClose={handleCloseToast}
              containerStyle="!max-w-none w-full"
            />
          </div>,
          document.body
        )}

      <ModalAsociarCuenta
        open={isModalAsociarOpen}
        setOpen={setIsModalAsociarOpen}
        onSubmit={handleModalSubmit}
        cargoOptions={cargoOptions}
        areaOptions={areaOptions}
        cuentas={todasLasCuentas}
      />

      {/* Modal de confirmación de baja de relación con una cuenta */}
      <ModalEliminarCuenta
        open={isEliminarModalOpen}
        setOpen={setIsEliminarModalOpen}
        razonSocial={cuentaAEliminar?.razonSocial ?? ''}
        onCancel={handleCancelarEliminar}
        onConfirm={handleConfirmarEliminar}
      />

      {/* PBI 81147 (D8): modal de carga de la DDJJ con el contrato de la fila de Mis cuentas.
          Los textos y las reglas (solo PDF, 2 MB) son propios de esta apertura; el banner
          del Shell sigue con los textos por defecto del modal. */}
      <ModalDdjj
        open={contratoDdjj !== null}
        contrato={contratoDdjj}
        onClose={() => setContratoDdjj(null)}
        onCargada={refreshContratos}
        textos={TEXTOS_DDJJ_MIS_CUENTAS}
        validacion={VALIDACION_DDJJ_MIS_CUENTAS}
      />
    </div>
  )
}
